<template>
    <div ref="containerRef" class="relative inline-block text-left">
        <button
            @click="toggleDropdown"
            type="button"
            :class="buttonClasses"
            :style="{ width: buttonWidth, 'min-height': '40.5px' }"
            :disabled="props.disabled || props.loading">
            <FunnelIcon v-if="!hasSelection" class="h-4 w-4 self-center mr-1" />
            <FunnelIcon v-else class="h-4 w-4 self-center mr-1 fill-green-400" />
            <span class="px-1">
                <slot name="displayTitle" :selectedCount="appliedSelection.length" :placeholder="props.placeholder">
                    {{ appliedSelection.length ? appliedSelection.length + ' selected' : props.placeholder }}
                </slot>
            </span>
            <ChevronDownIcon class="-mr-1 ml-2 h-5 w-5" />
        </button>

        <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95">
            <div v-if="dropdownOpen" ref="dropdownRef" class="absolute z-10 mt-2 w-80 rounded-md bg-white shadow-lg border border-gray-300">
                <div class="p-3 divide-y divide-gray-100 space-y-2">
                    <div class="mb-3">
                        <input
                            v-model="searchQuery"
                            type="text"
                            :placeholder="searchPlaceholder"
                            class="w-full border border-gray-300 rounded-md p-2"
                            :disabled="!isInteractive" />
                        <div class="flex gap-2 mt-2">
                            <button
                                type="button"
                                class="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-1 rounded-sm disabled:opacity-50"
                                @click="selectAllOptions"
                                :disabled="!isInteractive">
                                Select All
                            </button>
                            <button
                                type="button"
                                class="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-1 rounded-sm disabled:opacity-50"
                                @click="clearAllSelection"
                                :disabled="!isInteractive">
                                Clear All
                            </button>
                        </div>
                    </div>

                    <div class="max-h-60 overflow-y-auto my-3">
                        <div
                            v-for="option in filteredOptions"
                            :key="option[trackBy] ? option[trackBy] : JSON.stringify(option)"
                            class="flex items-center py-1.5">
                            <input
                                type="checkbox"
                                :id="option[trackBy] ? option[trackBy] : JSON.stringify(option)"
                                class="mr-2"
                                :value="option[trackBy] ? option[trackBy] : option"
                                :checked="isSelected(option)"
                                @change="toggleOption(option)"
                                :disabled="!isInteractive" />
                            <label :for="option[trackBy] ? option[trackBy] : JSON.stringify(option)" class="cursor-pointer w-full">
                                <slot name="option" :option="option">
                                    {{ option[optionLabel] ? option[optionLabel] : option }}
                                </slot>
                            </label>
                        </div>
                        <span v-if="!filteredOptions.length" class="text-gray-500 items-center text-sm mx-2">No options found</span>
                    </div>

                    <div v-if="enableOperators" class="py-3">
                        <div class="mb-1 text-sm font-medium text-gray-700">Operator</div>
                        <div class="flex gap-2">
                            <button
                                type="button"
                                :class="operatorButtonClass('or')"
                                @click="setOperator('or')"
                                :disabled="!tempSelection.length > 1 || !isInteractive">
                                OR
                            </button>
                            <button
                                type="button"
                                :class="operatorButtonClass('and')"
                                @click="setOperator('and')"
                                :disabled="!tempSelection.length > 1 || !isInteractive">
                                AND
                            </button>
                        </div>
                    </div>

                    <div class="pt-3 flex justify-between items-center">
                        <button
                            type="button"
                            class="text-cyan-950 hover:underline disabled:opacity-50"
                            @click="onManageEntity"
                            :disabled="!isInteractive">
                            <span class="flex items-center gap-1">
                                <PencilIcon class="h-4 w-4" />
                                <span>{{ manageButtonText }}</span>
                            </span>
                        </button>
                        <button
                            type="button"
                            class="bg-cyan-950 hover:bg-cyan-900 text-white text-sm font-medium py-1 px-3 rounded-sm disabled:opacity-50"
                            @click="applyChanges"
                            :disabled="!canApply || !isInteractive">
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { PencilIcon } from '@heroicons/vue/24/solid';
import { ChevronDownIcon, FunnelIcon } from '@heroicons/vue/20/solid';

const props = defineProps({
    options: { type: Array, required: true },
    modelValue: { type: Object, default: () => ({ selected: [], operator: 'or' }) },
    enableOperators: { type: Boolean, default: true },
    trackBy: { type: String, default: 'value' },
    optionLabel: { type: String, default: 'label' },
    placeholder: { type: String, default: 'Select options' },
    searchPlaceholder: { type: String, default: 'Search...' },
    manageButtonText: { type: String, default: 'Manage' },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    defaultInitialSelectedFilter: { type: Object, default: null },
    slimDropdownButton: { type: Boolean, default: false },
    buttonWidth: { type: String, default: 'fit-content' }
});

const emit = defineEmits(['update:modelValue', 'apply', 'onManageEntity']);

const containerRef = ref(null);
const dropdownRef = ref(null);
const dropdownOpen = ref(false);
const searchQuery = ref('');
const tempSelection = ref([...props.modelValue.selected]);
const tempOperator = ref(props.modelValue.operator || 'or');
const appliedSelection = ref([]);
const appliedOperator = ref(props.modelValue.operator || 'or');

// Flag to track any user interaction in the dropdown
const hasInteracted = ref(false);

const initializeSelection = () => {
    const newSelected = props.modelValue.selected || [];
    appliedSelection.value = props.options.filter((option) => {
        if (option.hasOwnProperty(props.trackBy)) {
            return newSelected.includes(option[props.trackBy]);
        }
        return newSelected.includes(option);
    });
    tempSelection.value = [...appliedSelection.value];
};

initializeSelection();

watch(
    () => props.modelValue,
    (newVal) => {
        const newSelected = newVal.selected || [];
        appliedSelection.value = props.options.filter((option) => {
            if (option.hasOwnProperty(props.trackBy)) {
                return newSelected.includes(option[props.trackBy]);
            }
            return newSelected.includes(option);
        });
        appliedOperator.value = newVal.operator || 'or';
        resetTemporary();
    },
    { deep: true }
);

const unsavedChanges = computed(() => {
    return JSON.stringify(tempSelection.value) !== JSON.stringify(appliedSelection.value) || tempOperator.value !== appliedOperator.value;
});

const canApply = computed(() => {
    return hasInteracted.value || unsavedChanges.value;
});

const hasSelection = computed(() => appliedSelection.value.length > 0);

const filteredOptions = computed(() => {
    if (!searchQuery.value) return props.options;
    return props.options.filter((option) =>
        String(option[props.optionLabel] || option)
            .toLowerCase()
            .includes(searchQuery.value.toLowerCase())
    );
});

function toggleOption(option) {
    const identifier = option.hasOwnProperty(props.trackBy) ? option[props.trackBy] : option;
    const index = tempSelection.value.findIndex((item) => {
        const itemId = item.hasOwnProperty(props.trackBy) ? item[props.trackBy] : item;
        return itemId === identifier;
    });
    if (index > -1) {
        tempSelection.value.splice(index, 1);
    } else {
        tempSelection.value.push(option);
    }
    hasInteracted.value = true;
}

function operatorButtonClass(op) {
    return [
        'px-3 py-1 rounded-sm border text-sm',
        tempOperator.value === op ? 'bg-cyan-950 text-white border-cyan-950' : 'bg-gray-100 text-gray-700 border-gray-300'
    ].join(' ');
}

function setOperator(op) {
    tempOperator.value = op;
    hasInteracted.value = true;
}

function applyChanges() {
    appliedSelection.value = [...tempSelection.value];
    appliedOperator.value = tempOperator.value;
    const finalSelection = tempSelection.value.map((option) => (option.hasOwnProperty(props.trackBy) ? option[props.trackBy] : option));
    emit('update:modelValue', { selected: finalSelection, operator: appliedOperator.value });
    emit('apply', { selected: finalSelection, operator: appliedOperator.value });
    searchQuery.value = '';
    dropdownOpen.value = false;
    hasInteracted.value = false;
}

function resetTemporary() {
    tempSelection.value = [...appliedSelection.value];
    tempOperator.value = appliedOperator.value;
    hasInteracted.value = false;
}

function cancelChanges() {
    resetTemporary();
    dropdownOpen.value = false;
}

function toggleDropdown() {
    if (!isInteractive.value) return;
    dropdownOpen.value ? cancelChanges() : (dropdownOpen.value = true);
}

function onManageEntity() {
    if (!isInteractive.value) return;
    emit('onManageEntity');
}

function clearAllSelection() {
    tempSelection.value = [];
    tempOperator.value = 'or';
    hasInteracted.value = true;
}

function selectAllOptions() {
    tempSelection.value = [...props.options];
    hasInteracted.value = true;
}

const isInteractive = computed(() => !props.disabled && !props.loading);

const buttonClasses = computed(() => {
    return [
        props.disabled || props.loading ? 'bg-gray-100 opacity-[0.7]' : 'bg-white hover:bg-gray-50',
        props.slimDropdownButton ? 'px-3' : 'px-4',
        'inline-flex min-w-max w-full justify-center items-center rounded-md border border-gray-300 text-sm font-medium text-gray-700 shadow-xs'
    ].join(' ');
});

function isSelected(option) {
    const identifier = option.hasOwnProperty(props.trackBy) ? option[props.trackBy] : option;
    return (
        tempSelection.value.findIndex((item) => {
            const itemId = item.hasOwnProperty(props.trackBy) ? item[props.trackBy] : item;
            return itemId === identifier;
        }) > -1
    );
}

function handleClickOutside(event) {
    if (containerRef.value && !containerRef.value.contains(event.target)) {
        if (dropdownOpen.value) cancelChanges();
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});
onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped></style>
